import React, { useCallback, useMemo, useRef } from "react";
import { motion, type HTMLMotionProps } from "framer-motion";
import { Box, BoxProps } from "@mui/material";
import { Container, Item } from "./FlexOrderCarousel.styled";
import { useEqualItemWidths } from "../../hooks/useEqualItemWidths";

type AnimationPreset = "spring" | "gentle" | "snappy" | "tween" | "instant";
type LayoutMode = "full" | "position";

export const MotionItem = motion.create(Item) as React.ComponentType<
  BoxProps & HTMLMotionProps<"div">
>;

export type FlexOrderCarouselProps = {
  orders?: number[];
  onOrderChange?: (idx: number) => void;
  itemWidth?: number | string;
  itemClassName?: string;
  shift?: boolean;
} & React.ComponentProps<typeof Container>;

const PRESETS: Record<AnimationPreset, any> = {
  spring: { type: "spring", stiffness: 500, damping: 100 },
  gentle: { type: "spring", stiffness: 250, damping: 30 },
  snappy: { type: "spring", stiffness: 700, damping: 25 },
  tween: { type: "tween", duration: 0.35, ease: [0.2, 0.8, 0.2, 1] },
  instant: { duration: 0 },
};

export function FlexOrder(props: FlexOrderCarouselProps) {
  const {
    children,
    orders: propsOrders,
    itemWidth,
    onOrderChange,
    itemClassName,
    shift = true,
    ...rest
  } = props;

  const { sx, ...restWithNoSx } = rest;
  const items = React.Children.toArray(children);

  const orders = useMemo(() => {
    if (propsOrders && propsOrders.length === items.length) return propsOrders;
    return items.map((_, i) => i);
  }, [propsOrders, items]);

  const handleClick = useCallback(
    (idx: number) => {
      onOrderChange?.(idx);
    },
    [onOrderChange],
  );

  const basis =
    itemWidth == null
      ? undefined
      : typeof itemWidth === "number"
        ? `${itemWidth}px`
        : itemWidth;

  const categoriesRef = useRef(null);
  const { calculatedWidth } = useEqualItemWidths({
    selector: ".item",
    containerRef: categoriesRef,
  } as any);

  return (
    <Container
      ref={categoriesRef}
      {...restWithNoSx}
      sx={{
        transform:
          items.length % 2 === 0 && shift
            ? `translateX(-${50 / items.length}%)`
            : undefined,
        ...sx,
      }}
    >
      {items.map((child, i) => {
        const k = (React.isValidElement(child) && child.key) ?? i;
        const order = orders[i] ?? i;

        return (
          <MotionItem
            className={itemClassName}
            key={k as React.Key}
            onClick={() => handleClick(i)}
            layout={false}
            transition={PRESETS.instant}
            sx={{
              order,
              flex: "0 0 auto",
            }}
          >
            <Box sx={{ width: calculatedWidth }}>{child}</Box>
          </MotionItem>
        );
      })}
    </Container>
  );
}
